import { z } from 'zod';
import { AuditedFields, IsoDateTime, Uuid, Version } from './common.js';
/**
 * Folio de séjour (ADR-018, DS-04) : le compte des charges posées pendant un
 * séjour, chacune figée avec sa provenance (module source, commande, point de
 * vente, auteur, horodatage, bon signé).
 *
 * Le folio n'a pas de paiement propre : l'encaissement reste celui du séjour.
 */
export const FolioStatus = z.enum(['OPEN', 'CLOSED']);
/** `POS_ORDER` : commande Restaurant / Bar imputée ; `MANUAL` : charge saisie par la réception. */
export const FolioChargeKind = z.enum(['POS_ORDER', 'MANUAL']);
export const FolioChargeStatus = z.enum(['POSTED', 'VOIDED']);
/** Article figé d'une charge (les lignes de la commande au moment de l'imputation). */
export const FolioChargeLine = z.object({
    label: z.string(),
    quantity: z.number(),
    amount: z.number(),
    options: z.array(z.string()),
});
export const FolioTaxDetail = z.object({
    code: z.string(),
    label: z.string(),
    rate: z.number(),
    isInclusive: z.boolean(),
    base: z.number(),
    amount: z.number(),
});
export const FolioCharge = z
    .object({
    id: Uuid,
    folioId: Uuid,
    reservationId: Uuid,
    kind: FolioChargeKind,
    status: FolioChargeStatus,
    /** Provenance : `pos` pour une commande, `null` pour une charge manuelle. */
    sourceModule: z.string().nullable(),
    sourceType: z.string().nullable(),
    sourceId: Uuid.nullable(),
    /** Référence lisible de la source (`CMD-2026-000012`). */
    sourceReference: z.string().nullable(),
    outletId: Uuid.nullable(),
    outletName: z.string().nullable(),
    label: z.string(),
    description: z.string().nullable(),
    quantity: z.number(),
    unitPrice: z.number(),
    /** Montant dû par le client pour cette charge (taxes comprises pour une commande). */
    amount: z.number(),
    taxTotal: z.number(),
    taxDetails: z.array(FolioTaxDetail),
    /** Taxes déjà calculées ligne à ligne (commande POS) : hors assiette des règles TAX du séjour. */
    taxSettled: z.boolean(),
    taxRuleCodes: z.array(z.string()),
    lines: z.array(FolioChargeLine),
    currencyCode: z.string().length(3),
    postedAt: IsoDateTime,
    postedById: Uuid.nullable(),
    postedByName: z.string().nullable(),
    voidedAt: IsoDateTime.nullable(),
    voidedByName: z.string().nullable(),
    voidReason: z.string().nullable(),
    /** Bon de transfert signé et scanné (document `SIGNED_SLIP`). */
    signatureDocumentId: Uuid.nullable(),
    signedAt: IsoDateTime.nullable(),
    notes: z.string().nullable(),
    version: Version,
})
    .merge(AuditedFields);
/** Résumé du folio porté par la fiche réservation. */
export const StayFolioSummary = z.object({
    id: Uuid,
    status: FolioStatus,
    chargesCount: z.number().int().nonnegative(),
    chargesTotal: z.number(),
    openedAt: IsoDateTime,
    closedAt: IsoDateTime.nullable(),
});
export const FolioDetail = z.object({
    reservationId: Uuid,
    reservationReference: z.string(),
    currencyCode: z.string().length(3),
    /** `null` tant qu'aucune charge n'a été posée. */
    folio: StayFolioSummary.nullable(),
    charges: z.array(FolioCharge),
    /** Charges actives, nombre et montant. */
    chargesCount: z.number().int().nonnegative(),
    chargesTotal: z.number(),
});
/** Charge manuelle (minibar, blanchisserie, dégât…) posée par la réception. */
export const CreateFolioChargeRequest = z.object({
    label: z.string().min(1).max(160),
    description: z.string().max(500).nullable().optional(),
    quantity: z.number().positive().max(9999).default(1),
    unitPrice: z.number().nonnegative(),
    /** Codes de règles de taxe applicables (moteur ADR-009) ; vide = règles du séjour. */
    taxRuleCodes: z.array(z.string().min(1).max(40)).max(10).default([]),
    notes: z.string().max(500).nullable().optional(),
});
export const VoidFolioChargeRequest = z.object({
    reason: z.string().min(5).max(500),
    version: Version,
});
/** Rattachement du bon signé (document `SIGNED_SLIP` du séjour) à la charge. */
export const AttachFolioSignatureRequest = z.object({
    documentId: Uuid,
    version: Version,
});
/** Séjour en cours, vue minimale pour le point de vente (« la chambre 204 »). */
export const InHouseStay = z.object({
    reservationId: Uuid,
    reference: z.string(),
    propertyId: Uuid,
    resourceName: z.string(),
    resourceInternalRef: z.string().nullable(),
    guestName: z.string(),
    arrivalAt: IsoDateTime,
    departureAt: IsoDateTime,
    /** Charges déjà imputées au folio (montant), pour information. */
    folioTotal: z.number(),
});
export const ListInHouseStaysQuery = z.object({
    propertyId: Uuid.optional(),
    /** Numéro de chambre, nom du client ou référence. */
    q: z.string().max(100).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
//# sourceMappingURL=folio.js.map